/**
 * How many records **this device, this file** can carry — A-3, step 3.
 *
 * ⚠️ **This replaces a published constant with a derivation, and that is the point.** The old
 * answer was *"~1–2M"* in an NFR table: unmeasured, describing DuckDB-WASM that is not built, and
 * — even had it been measured — wrong for most files, because footprint scales with **row width**.
 * `packages/match-core`'s footprint measurement puts the spread at roughly **166 B/record at 4
 * columns and 4,657 B/record at 90**. That is 28×. No constant can serve both.
 *
 * So the ceiling is a division, with one half measured in CI and the other reported by the device:
 *
 *     ceiling ≈ (device memory × safety factor) ÷ (bytes per record at THIS file's width)
 *
 * This implements [ADR-067 (4)](../../../../docs/platform/11-technical-architecture.md#8--architecture-decision-records)
 * and IA 38 as already written: *"the estimate and its basis are always shown — never a bare
 * verdict."*
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ⚠️ HOW OFTEN THIS ACTUALLY BINDS — MEASURED, AND LESS THAN IT LOOKS
 *
 * Computed from the constants below: the derived figure only falls under the fixed cap at
 *
 *     0.5 GB → from ~53 columns · 1 GB → ~104 · 2 GB → ~206 · 4 GB and above → never, realistically
 *
 * So **on a normal laptop the answer is always 50,000**, and this module changes nothing about the
 * limit. Said plainly rather than left for someone to discover, because machinery that implies it
 * is doing more than it does is its own small dishonesty — the same failure as the number it
 * replaces, one level up.
 *
 * What it genuinely buys: the screen can state **what the limit is made of** instead of asserting
 * one, and a low-end phone handed a very wide export is protected. Those are worth having. A
 * general uplift is not what this is.
 */
import { MAX_RECORDS } from './store.js';

/**
 * Measured in `packages/match-core/test/ceiling.test.ts`, in **serialised** bytes — a LOWER bound,
 * because a JS string carries object overhead beyond its characters.
 *
 * ⚠️ Being a lower bound makes the derived ceiling *optimistic*, which is why `SAFETY_FACTOR`
 * below is deliberately harsh. The two errors point in opposite directions on purpose.
 */
const BYTES_PER_RECORD_BY_WIDTH: readonly (readonly [width: number, bytes: number])[] = [
  [4, 166],
  [20, 985],
  [90, 4657],
];

/**
 * A quarter of reported memory.
 *
 * Not timidity: `navigator.deviceMemory` reports *system* RAM, of which a browser tab gets a
 * fraction, of which the JS heap gets a fraction again. And the two failure modes are not
 * symmetric — underestimating costs a truncation notice that states its own basis, while
 * overestimating gets the tab reaped by the OS with no result and no explanation, which is the
 * one outcome a first-run test drive cannot survive.
 */
export const SAFETY_FACTOR = 0.25;

/** Linear interpolation between the measured widths; clamped outside them rather than extrapolated. */
export function bytesPerRecordAt(width: number): number {
  const points = BYTES_PER_RECORD_BY_WIDTH;
  if (width <= points[0]![0]) return points[0]![1];
  const last = points[points.length - 1]!;
  if (width >= last[0]) {
    /* ⚠️ Extrapolated linearly past the widest measurement rather than clamped. Clamping would
       make a 200-column export look no heavier than a 90-column one, which is exactly the
       "one number for every file" error this module exists to remove. */
    const [w1, b1] = points[points.length - 2]!;
    const perColumn = (last[1] - b1) / (last[0] - w1);
    return last[1] + (width - last[0]) * perColumn;
  }
  for (let i = 1; i < points.length; i += 1) {
    const [w1, b1] = points[i - 1]!;
    const [w2, b2] = points[i]!;
    if (width <= w2) return b1 + ((width - w1) / (w2 - w1)) * (b2 - b1);
  }
  return last[1];
}

/**
 * What the device says it has, in GB, or `null`.
 *
 * ⚠️ `navigator.deviceMemory` is **Chromium-only** and deliberately coarse — rounded to a power of
 * two and clamped, so it cannot be used for fingerprinting. Safari and Firefox do not implement it
 * at all.
 *
 * ⚠️ **Do not assume an upper bound of 8.** An earlier version of this comment asserted the value
 * was "capped at 8 by design"; the browser this was verified in **reports 16**. The clamp is
 * implementation-defined and has moved. Nothing here depends on the bound — it is a divisor — but
 * a reader who trusted that sentence would have written a check that silently rejects real
 * hardware. Verified by reading `navigator.deviceMemory` in a browser rather than recalling MDN.
 *
 * `null` is returned rather than a default, and every caller must handle it. A fabricated device
 * budget would reintroduce precisely the invented number this whole change removes — and it would
 * be worse than the old one, because it would look personalised.
 */
export function deviceMemoryGb(): number | null {
  const value = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  return typeof value === 'number' && value > 0 ? value : null;
}

export interface Capacity {
  /** The cap actually applied to this file. */
  limit: number;
  /** Where it came from — so the screen can say, rather than announcing a bare number. */
  basis: 'derived' | 'fixed_cap' | 'unknown_device';
  /** Present when the device reported its memory. */
  deviceGb: number | null;
  /** Bytes per record at this file's width — the divisor, shown as part of the basis. */
  bytesPerRecord: number;
  columns: number;
}

/**
 * The cap for a file of a given width on this device.
 *
 * ⚠️ **Never above `MAX_RECORDS`.** The derived figure answers "will this fit in memory"; it says
 * nothing about the other reasons a very large run is a bad idea in a browser tab — a run that
 * finishes beats a bigger one that hangs, and the fixed cap is what keeps that promise. So the
 * derivation can only ever make the limit *lower*, which is the safe direction for a number
 * derived from a deliberately coarse input.
 */
export function capacityFor(columns: number, memoryGb: number | null = deviceMemoryGb()): Capacity {
  const bytesPerRecord = bytesPerRecordAt(columns);
  if (memoryGb === null) {
    return { limit: MAX_RECORDS, basis: 'unknown_device', deviceGb: null, bytesPerRecord, columns };
  }
  const derived = Math.floor((memoryGb * 1024 ** 3 * SAFETY_FACTOR) / bytesPerRecord);
  return derived < MAX_RECORDS
    ? { limit: derived, basis: 'derived', deviceGb: memoryGb, bytesPerRecord, columns }
    : { limit: MAX_RECORDS, basis: 'fixed_cap', deviceGb: memoryGb, bytesPerRecord, columns };
}

/**
 * The sentence shown to the reader — the **basis**, not just the verdict.
 *
 * IA 38 requires this, and F06's savings estimate set the precedent: publish the formula and the
 * inputs, so a figure can be argued with rather than merely believed.
 */
export function explainCapacity(c: Capacity): string {
  const perRecord = `${Math.round(c.bytesPerRecord)} bytes per row at ${c.columns} columns`;
  if (c.basis === 'unknown_device') {
    return (
      `Limited to ${c.limit.toLocaleString()} rows. This browser does not report how much memory ` +
      `it has, so we cannot size this to your device and use a fixed limit instead (${perRecord}).`
    );
  }
  if (c.basis === 'fixed_cap') {
    return (
      `Limited to ${c.limit.toLocaleString()} rows — a fixed cap, not a memory limit. Your device ` +
      `reports ${c.deviceGb} GB, which at ${perRecord} would hold more, but a run that finishes ` +
      `beats a bigger run that hangs the tab.`
    );
  }
  return (
    `Limited to ${c.limit.toLocaleString()} rows: ${c.deviceGb} GB reported, of which we use a ` +
    `quarter, divided by ${perRecord}. Wider files hold fewer rows.`
  );
}
